<script setup lang="ts">
import {ref, watch} from "vue";
import {computed} from "vue";
import CustomColorChip from "@/components/Commons/Colors/CustomColorChip.vue";
import {COLOR_PICKER_NONE_COLOR_VALUE} from "@/lib/constants/colors";
import {HTMLElementStyleObject} from "@/interfaces";

interface CustomColorPickerProps {
  colors: string[];
  currentColor?: string | null;
  columnsCount?: number;
  bordered?: boolean;
}

const props = withDefaults(defineProps<CustomColorPickerProps>(), {
  bordered: false,
});

const emit = defineEmits<{
  (e: "update:currentColor", color: string);
}>();

const selectedColor = ref<string | null>(props.currentColor);

const hasNoneColor = computed<boolean>(() =>
  props.colors.includes(COLOR_PICKER_NONE_COLOR_VALUE),
);

const gridStyle = computed<HTMLElementStyleObject>(() => ({
  "grid-template-columns": `repeat(${
    props.columnsCount ?? props.colors.length
  }, minmax(0, 1fr))`,
}));
/**
 * updates the selected color when the prop value changes
 * used to default the value upon CustomColorDefiner menu "disappearance"
 */
watch(
  () => props.currentColor,
  (newColor: string) => {
    selectedColor.value = newColor;
  },
);

function isSelectedColor(color: string): boolean {
  return selectedColor.value === color;
}
function selectColor(color: string): void {
  selectedColor.value =
    isSelectedColor(color) && hasNoneColor.value
      ? COLOR_PICKER_NONE_COLOR_VALUE
      : color;
  emit("update:currentColor", selectedColor.value);
}
</script>

<template>
  <div class="custom-color-picker" :style="gridStyle">
    <CustomColorChip
      v-for="color in colors"
      :key="`color-${color}`"
      :color="color"
      :is-selected="isSelectedColor(color)"
      :is-bordered="bordered"
      @click="() => selectColor(color)"
    />
  </div>
</template>

<style scoped lang="scss">
.custom-color-picker {
  display: grid;
  gap: 12px;
}
</style>
